// Sentences tab of the Dhātu lab: every cell of the chosen table used with its subject pronoun (अहं पढामि। …),
// the feminine subject for the अन्य पुरुष cells, and मा + the command for forbidding. Every word is tappable.

import type { FC, ReactNode } from 'react'
import type { Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import Link from 'next/link'
import { ArrowRight, Grid2x2, Network } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AV, formOf } from '@/prakrit/lib/grammar/formref'
import { BASIC_TENSES } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL, TENSE_LABEL } from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { TriLine } from '@/prakrit/components/common/tri-line'
import { CELL_ORDER } from '@/prakrit/components/grammar/labs/verb-data'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import {
    cellGloss,
    coreForm,
    forbidMeaning,
    sentenceMeaning,
    sentenceOf
} from '@/prakrit/components/grammar/dhatu/meaning'

interface SentencesViewProps {
    root: LabRoot
    tense: Tense
    person: Person
    number: Num
    onSelect: (person: Person, number: Num) => void
}

const Gloss: FC<{ root: LabRoot, tense: Tense, p: Person, n: Num, fem?: boolean }> = ({ root, tense, p, n, fem = false }): ReactNode => {
    const gloss = cellGloss(root, tense, p, n, fem ? 'stri' : 'pu')

    return <Bi hi={gloss.hi} en={gloss.en} className='gap-0.5 text-sm text-muted-foreground' />
}

export const SentencesView: FC<SentencesViewProps> = ({ root, tense, person, number, onSelect }): ReactNode => {
    const forbid = tense === 'vidhi' ? formOf(AV('मा')) : ''
    const gridHref = `/grammar/purush-vachan?root=${encodeURIComponent(root.root)}${BASIC_TENSES.includes(tense) ? `&tense=${tense}` : ''}`

    return (
        <div className='flex flex-col gap-5'>
            <p className='text-sm text-muted-foreground'>
                <Bi
                    hi={`${TENSE_LABEL[tense].hi}: हर खाने का रूप उसके कर्ता के साथ। किसी भी शब्द पर टैप करके उसका पूरा विवरण देखिए।`}
                    en={`${TENSE_LABEL[tense].en}: each form with its subject. Tap any word to see its full analysis.`}
                />
            </p>

            <ul className='grid gap-3 md:grid-cols-2'>
                {CELL_ORDER.map(([p, n]) => {
                    const form = coreForm(root, tense, p, n)
                    const active = p === person && n === number

                    if (!form) return null

                    const meaning = sentenceMeaning(root, tense, p, n)
                    const femSubject = p === 'anya' ? sentenceOf(root, tense, p, n, 'stri', form) : ''
                    const femMeaning = femSubject ? sentenceMeaning(root, tense, p, n, 'stri') : null
                    const forbidMeans = forbid && p === 'madhyam' ? forbidMeaning(root, n) : null

                    return (
                        <li key={`${p}.${n}`} className={cn(PANEL, 'flex flex-col gap-3 p-4 sm:p-5', active && 'border-saffron ring-1 ring-saffron/40')}>
                            <button
                                type='button'
                                aria-pressed={active}
                                onClick={() => onSelect(p, n)}
                                className='inline-flex min-h-tap items-center gap-2 self-start rounded-full border border-border bg-secondary/60 px-3 text-xs font-medium transition-colors hover:border-saffron/60'
                            >
                                <span lang='hi' className='hindi'>{PERSON_LABEL[p].hi} · {NUMBER_LABEL[n].hi}</span>
                                <span lang='en' data-ex='en' className='text-muted-foreground'>{NUMBER_LABEL[n].en}</span>
                            </button>

                            <TriLine pra={sentenceOf(root, tense, p, n, 'pu', form)} hi={meaning?.hi} en={meaning?.en} size='md' />
                            {!meaning && <Gloss root={root} tense={tense} p={p} n={n} />}

                            {femSubject && (
                                <div className='flex flex-col gap-1 border-t border-border pt-3'>
                                    <span className='text-xs text-muted-foreground'>
                                        <span lang='hi' className='hindi'>स्त्रीलिंग कर्ता</span>
                                        <span aria-hidden='true'> · </span>
                                        <span lang='en'>feminine subject</span>
                                    </span>
                                    <TriLine pra={femSubject} hi={femMeaning?.hi} en={femMeaning?.en} size='sm' />
                                    {!femMeaning && <Gloss root={root} tense={tense} p={p} n={n} fem />}
                                </div>
                            )}

                            {forbid && p === 'madhyam' && (
                                <div className='flex flex-col gap-1 rounded-2xl border border-dashed border-border px-4 py-3'>
                                    <span className='text-xs text-muted-foreground'>
                                        <span lang='hi' className='hindi'>मना करना: {forbid} + रूप</span>
                                        <span aria-hidden='true'> · </span>
                                        <span lang='en'>forbidding: {forbid} + the form</span>
                                    </span>
                                    <TriLine pra={`${forbid} ${form}।`} hi={forbidMeans?.hi} en={forbidMeans?.en} size='sm' />
                                </div>
                            )}
                        </li>
                    )
                })}
            </ul>

            <div className='grid gap-3 sm:grid-cols-2'>
                <Link href={gridHref} className={cn(PANEL, 'group flex min-h-tap items-center gap-3 px-5 py-4 transition-colors hover:border-saffron')}>
                    <Grid2x2 className='size-5 shrink-0 text-saffron-strong' aria-hidden='true' />
                    <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                        <span lang='hi' className='hindi font-semibold'>पुरुष × वचन तालिका में देखें</span>
                        <span lang='en' className='text-sm text-muted-foreground'>See it on the person × number grid</span>
                    </span>
                    <ArrowRight className='size-4 shrink-0 text-saffron-strong transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                </Link>
                <Link href='/grammar/karak' className={cn(PANEL, 'group flex min-h-tap items-center gap-3 px-5 py-4 transition-colors hover:border-saffron')}>
                    <Network className='size-5 shrink-0 text-saffron-strong' aria-hidden='true' />
                    <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                        <span lang='hi' className='hindi font-semibold'>वाक्य का विश्लेषण करें</span>
                        <span lang='en' className='text-sm text-muted-foreground'>Parse a longer sentence, word by word</span>
                    </span>
                    <ArrowRight className='size-4 shrink-0 text-saffron-strong transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                </Link>
            </div>
        </div>
    )
}
